'use client'
import { createContext, useContext, useRef, type ReactNode } from 'react'
import type { AlertType, AlertVariant } from '@/components/alert'

type ToastData = {
    id?: number
    content: ReactNode
    type?: AlertType
    variant?: AlertVariant
    timeout?: number
}

type ToastsState = ToastData[]

type Subscription = (toasts: ToastData[]) => void
type SubscribeFunction = (callback: (toasts: ToastData[]) => void) => { unsubscribe: () => void }
type UnSubscribeFunction = (callback: (toasts: ToastData[]) => void) => void
type AddFunction = (toastData: ToastData) => {
    remove: () => void
    update: (toast: ToastData) => void
}

type ToastsContextType = {
    add: (toast: ToastData) => { remove: () => void; update: (toast: ToastData) => void }
    remove: (id?: number) => void
    update: (toast: ToastData) => void
    subscribe: SubscribeFunction
    unsubscribe: UnSubscribeFunction
}

let counter = 0
const getId = (): number => {
    counter += 1
    return counter
}

const ToastsContext = createContext<ToastsContextType>({
    add: () => ({
        remove: () => null,
        update: () => null,
    }),
    remove: () => null,
    update: () => null,
    subscribe: () => ({ unsubscribe: () => null }),
    unsubscribe: () => null,
})

function ToastsProvider(props: { children?: ReactNode }): JSX.Element {
    const { children } = props
    const toastsRef = useRef<ToastsState>([])
    const subscribtionsRef = useRef<Subscription[]>([])

    const add: AddFunction = (toastData: ToastData) => {
        const id = getId()
        toastsRef.current = [...toastsRef.current, { ...toastData, id }]
        executeSubscriptions()
        return {
            remove: () => {
                remove(id)
            },
            update: (toast: ToastData) => {
                update({ ...toast, id })
            },
        }
    }

    const remove = (id?: number): void => {
        if (!id) return
        toastsRef.current = [...toastsRef.current.filter((toast) => toast.id !== id)]
        executeSubscriptions()
    }

    const update = (toastData: ToastData): void => {
        toastsRef.current = [...toastsRef.current.map((toast) => (toast.id === toastData.id ? toastData : toast))]
        executeSubscriptions()
    }

    const executeSubscriptions = (): void => {
        subscribtionsRef.current.forEach((sub) => {
            sub(toastsRef.current)
        })
    }

    const subscribe: SubscribeFunction = (callback: (toasts: ToastData[]) => void) => {
        if (!subscribtionsRef.current.includes(callback)) {
            subscribtionsRef.current.push(callback)
        }
        return {
            unsubscribe: () => {
                unsubscribe(callback)
            },
        }
    }

    const unsubscribe: UnSubscribeFunction = (callback: (toasts: ToastData[]) => void) => {
        subscribtionsRef.current = subscribtionsRef.current.filter((sub) => sub !== callback)
    }

    return (
        <ToastsContext.Provider value={{ add, remove, update, subscribe, unsubscribe }}>
            {children}
        </ToastsContext.Provider>
    )
}

ToastsProvider.displayName = 'ToastsContextProvider'

const useToaster = (): ToastsContextType => useContext(ToastsContext)

export { ToastsProvider, useToaster, type ToastData }
